import { Dynamic } from "solid-js/web";
import type { ComponentProps } from "solid-js";
import { cn } from "@aula/shared";

/**
 * Aula brand mark — Nova, Aula's four-point sparkle-star, with an
 * "aha" flash at the center. Rendered in the brand's own grape→berry gradient
 * (constant in light and dark, independent of currentColor). The gradient ID is
 * suffixed so multiple logos on a page do not collide
 * (e.g. sidebar + auth). Distinctive, cheerful, and legible at any size.
 */
let markCounter = 0;

function LogoMark(props: ComponentProps<"svg">) {
  const gradId = `aula-mark-${(markCounter += 1)}`;
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <defs>
        <linearGradient id={gradId} x1="5" y1="3" x2="27" y2="29" gradientUnits="userSpaceOnUse">
          <stop stop-color="oklch(0.62 0.232 322)" />
          <stop offset="0.55" stop-color="oklch(0.64 0.22 350)" />
          <stop offset="1" stop-color="oklch(0.68 0.2 24)" />
        </linearGradient>
      </defs>
      {/* Estrella-chispa de 4 puntas (Nova) */}
      <path
        d="M16 2.4c1 3.4 2.2 5.8 4 7.6s4.2 3 7.6 4c-3.4 1-5.8 2.2-7.6 4s-3 4.2-4 7.6c-1-3.4-2.2-5.8-4-7.6s-4.2-3-7.6-4c3.4-1 5.8-2.2 7.6-4s3-4.2 4-7.6Z"
        fill={`url(#${gradId})`}
      />
      {/* Destello "aha" */}
      <circle cx="16" cy="16" r="2.1" fill="white" opacity="0.92" />
    </svg>
  );
}

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Element used for the wordmark in the `lg` variant. Defaults to `h1`, but
   * auth cards pass `p` so the page keeps a single brand `h1` (the auth brand
   * panel) and the cards expose their own `h2` heading instead.
   */
  as?: "h1" | "p";
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";
  const wordmarkTag = () => props.as ?? "h1";

  return (
    <div
      class={cn(
        "flex items-center gap-2.5 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <LogoMark class="h-8 w-8 shrink-0 drop-shadow-sm" />
          <span class="aula-wordmark text-xl">Aula</span>
        </>
      ) : (
        <>
          <LogoMark class="h-16 w-16 mb-2 shrink-0 drop-shadow-md transition-transform hover:scale-105 active:scale-95 duration-300" />
          <Dynamic component={wordmarkTag()} class="aula-wordmark text-3xl">
            Aula
          </Dynamic>
        </>
      )}
    </div>
  );
}
